import { useDashboardData } from '../../components/dashboard/useDashboardData';
import { CardDeck } from '../../components/landing/CardDeck';
import { DemoCard } from '../../components/landing/DemoCard';
import { HeroCard } from '../../components/landing/HeroCard';
import { HowItWorksCard } from '../../components/landing/HowItWorksCard';
import Styles from '../../components/landing/Landing.module.css';
import { LandingConstants } from '../../components/landing/LandingConstants';
import { LandingHeader } from '../../components/landing/LandingHeader';
import { PainCard } from '../../components/landing/PainCard';
import { PricingCard } from '../../components/landing/PricingCard';
import { RoiCard } from '../../components/landing/RoiCard';
import { SignupCard } from '../../components/landing/SignupCard';
import { Constants } from '../../internal/Constants';
import { useLocale } from '../../providers/LocaleProvider';
import { useAuth } from '@app/engine';
import { GetCurrencyForCountry, GetSupportedCountriesList, ResolveCurrencyAmount, ResolveSupportedCurrency } from '@shared/common';
import { BillingClient, GetUserGeoCountry, NicheContentClient, Telemetry, TenantClient } from '@shared/frontend';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ICardDeckHandle } from '../../components/landing/CardDeck';
import type { INicheContentDTO, IPlanDTO } from '@shared/common';

/** File-local constants for the Landing controller. */
const localConstants = {
  topCardIndex: 0,
  minorUnitsPerMajor: 100,
  // Headroom left above a card when jumping to it, so the fixed header doesn't cover the card top.
  headerOffsetRem: 6,
  landingViewTelemetryKey: 'landing_view',
  landingCardTelemetryKeyPrefix: 'landing_card_view_',
  emptyPlans: [] as IPlanDTO[],
  emptyNiches: [] as INicheContentDTO[]
} as const;

/** Telemetry keys for each landing card. */
const landingCardTelemetryKeys = ['hero', 'demo', 'pain', 'how_it_works', 'roi', 'pricing', 'signup'] as const;

/**
 * Public marketing landing page. Composes a pinned header overlay above a flow-scroll deck of seven cards.
 *
 * @returns The landing page element.
 */
export function Landing() {
  const deckRef = useRef<ICardDeckHandle>(null);
  const navigate = useNavigate();
  // Subscribe to the active language so a translation swap re-renders the whole card subtree.
  const { langVersion, selectedLang } = useLocale();
  const { isAuthenticated } = useAuth();
  const [selectedPlanCode, setSelectedPlanCode] = useState<string>('');
  // Card 6 country selection, lifted here so the pricing card's currency reacts to it. Defaults to the visitor's session (geo) country, but restores a country staged mid-onboarding so the landing
  // and onboarding forms stay in sync when the visitor navigates back.
  const [selectedCountry, setSelectedCountry] = useState<string>(() => {
    const staged = sessionStorage.getItem(Constants.SetupBillingCountryStorageKey);
    const supportedCountryCodes = GetSupportedCountriesList().map((country) => country.code);
    if (staged && supportedCountryCodes.includes(staged)) return staged;
    return GetUserGeoCountry();
  });
  const [activeCardIndex, setActiveCardIndex] = useState<number>(localConstants.topCardIndex);
  const seenLandingCards = useRef<Set<string>>(new Set());

  const plansResult = useDashboardData<IPlanDTO[]>((signal) => BillingClient.ListPublicPlans(signal, selectedLang), [selectedLang]);
  const plans = plansResult.data ?? localConstants.emptyPlans;
  const nichesResult = useDashboardData<INicheContentDTO[]>((signal) => NicheContentClient.ListPublicNiches(signal, selectedLang), [selectedLang]);
  const niches = nichesResult.data ?? localConstants.emptyNiches;
  // Best-effort tenant country for authenticated visitors: it takes precedence over the Card 6 selection so a logged-in tenant always sees their own currency.
  const tenantProfileResult = useDashboardData(
    isAuthenticated
      ? async (signal) => {
          const me = await TenantClient.GetMe(signal);
          return me ? TenantClient.GetProfile(signal, me.tenantID) : null;
        }
      : null,
    [isAuthenticated]
  );
  const tenantCountry = tenantProfileResult.data?.countryCode ?? '';
  // Mid-onboarding country bridge: while authenticated, the onboarding "Create your business" step (AccountStep) stages the chosen country in session storage so the public pricing reflects it before
  // the tenant profile (and its billing currency) exists. Read ONLY when authenticated, so a stale staged value can never override a logged-out visitor's Card 6 selection. Re-read when auth resolves
  // (it settles after first paint) so the pricing updates the moment an onboarding visitor lands here.
  useEffect(() => {
    if (!isAuthenticated) return;
    const staged = sessionStorage.getItem(Constants.SetupBillingCountryStorageKey) ?? '';
    const supportedCountryCodes = GetSupportedCountriesList().map((country) => country.code);
    if (staged && supportedCountryCodes.includes(staged) && staged !== selectedCountry) {
      setSelectedCountry(staged);
    }
  }, [isAuthenticated, selectedCountry]);
  // Prices show in the visitor's local currency. Country precedence: tenant record (provisioned), then the in-progress onboarding selection (authenticated, pre-provision), then the Card 6 selection /
  // geo (logged-out visitor). Unsupported codes fall through; the final fallback is the first supported country (USD).
  const visitorCurrency = useMemo(() => {
    const supportedCountryCodes = GetSupportedCountriesList().map((country) => country.code);
    const effectiveCountry = [tenantCountry, selectedCountry].find((code) => supportedCountryCodes.includes(code)) ?? '';
    const countryCode = effectiveCountry || (supportedCountryCodes[localConstants.topCardIndex] ?? '');
    return GetCurrencyForCountry(countryCode);
  }, [tenantCountry, selectedCountry]);

  // ROI calculator anchors on the CHEAPEST plan (its name + monthly fee), resolved into the visitor's currency exactly like the pricing cards (same per-plan override + USD fallback), so the
  // calculator never shows a USD price while the cards show CAD, and the plan name/price stay dynamic instead of a hardcoded "Pro / $447".
  const echoPlan = useMemo(() => {
    if (plans.length === 0) return null;
    const resolved = plans.map((plan) => {
      const effectiveCurrency = ResolveSupportedCurrency(plan.baseAmountByCurrency, plan.currency, visitorCurrency);
      const amountMinor = ResolveCurrencyAmount(plan.baseAmount, plan.baseAmountByCurrency, plan.currency, effectiveCurrency);
      return { name: plan.name, effectiveCurrency, amountMinor };
    });
    return resolved.reduce((cheapest, current) => (current.amountMinor < cheapest.amountMinor ? current : cheapest));
  }, [plans, visitorCurrency]);
  const echoMonthlyCost = echoPlan ? echoPlan.amountMinor / localConstants.minorUnitsPerMajor : LandingConstants.roiEchoMonthlyCostFallback;
  const echoPlanName = echoPlan?.name ?? '';
  const echoCurrency = echoPlan?.effectiveCurrency ?? visitorCurrency;

  useEffect(() => {
    Telemetry.FeatureUsed(localConstants.landingViewTelemetryKey);
  }, []);

  useEffect(() => {
    const cardKey = landingCardTelemetryKeys[activeCardIndex];
    if (!cardKey) return;
    const telemetryKey = `${localConstants.landingCardTelemetryKeyPrefix}${cardKey}`;
    if (seenLandingCards.current.has(telemetryKey)) return;
    seenLandingCards.current.add(telemetryKey);
    Telemetry.FeatureUsed(telemetryKey);
  }, [activeCardIndex]);

  const goToRoi = () => {
    Telemetry.FeatureUsed('landing_hero_calculate');
    deckRef.current?.goTo(LandingConstants.roiCardIndex, localConstants.headerOffsetRem);
  };
  // Leave header headroom so the Get Started CTA lands below the fixed header rather than underneath it.
  const goToSignup = () => deckRef.current?.goTo(LandingConstants.signupCardIndex, localConstants.headerOffsetRem);
  const goToLogin = () => {
    Telemetry.FeatureUsed('landing_header_login');
    navigate(Constants.Route.Home);
  };
  const goToDemo = () => {
    Telemetry.FeatureUsed('landing_header_demo');
    navigate(Constants.Route.AboutDemo);
  };

  // Clicking the logo scrolls the deck back to the top card.
  const onLogo = useCallback(() => {
    Telemetry.FeatureUsed('landing_header_logo');
    deckRef.current?.goTo(localConstants.topCardIndex);
  }, []);

  // Selecting a plan jumps to the signup card and pre-selects that plan in the form.
  const choosePlan = (planCode: string) => {
    Telemetry.FeatureUsed('landing_pricing_select_plan');
    setSelectedPlanCode(planCode);
    goToSignup();
  };

  const goToHeroSignup = () => {
    Telemetry.FeatureUsed('landing_header_get_started');
    goToSignup();
  };

  const recoverFromRoi = () => {
    Telemetry.FeatureUsed('landing_roi_recover');
    goToSignup();
  };

  const onDeckActiveChange = useCallback((index: number) => setActiveCardIndex(index), []);

  return (
    <div className={Styles.landingRoot} data-lang-version={langVersion}>
      <LandingHeader onLogo={onLogo} onLogin={goToLogin} onDemo={goToDemo} onGetStarted={goToHeroSignup} />
      <CardDeck ref={deckRef} onActiveChange={onDeckActiveChange}>
        <HeroCard niches={niches} onCalculate={goToRoi} />
        <DemoCard niches={niches} />
        <PainCard />
        <HowItWorksCard />
        <RoiCard niches={niches} echoMonthlyCost={echoMonthlyCost} echoPlanName={echoPlanName} currency={echoCurrency} onRecover={recoverFromRoi} />
        <PricingCard plans={plans} currency={visitorCurrency} onChoosePlan={choosePlan} />
        <SignupCard plans={plans} niches={niches} selectedPlanCode={selectedPlanCode} countryCode={selectedCountry} onCountryChange={setSelectedCountry} />
      </CardDeck>
    </div>
  );
}
